import React, { FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { FileRouteTypes } from '@tanstack/react-router';
import { Button } from '../components';
import { FormInput } from '../components/forms/FormInput';
import { PasswordInput } from '../components/forms/PasswordInput';
import { RequestPasswordReset, RequestSetPassword } from '../components/buttons/PasswordReset';
import { PatientDoc } from '@usacsmideo/types';
import usePatientDoc from '../hooks/usePatientDoc';
import { createPatient } from '../data/createPatient';
import * as Sentry from '@sentry/browser';
import { toast } from 'sonner';
import { ButtonGreen } from '../components/buttons';
import { getMultiFactorResolver, type MultiFactorError } from 'firebase/auth';
import { auth, db } from '../constants/firebaseModular';
import { doc, getDocFromServer } from 'firebase/firestore';
import { MfaChallengeForm } from '../components/auth/MfaChallengeForm';
import type { Resolver } from '../hooks/useMfaChallenge';
import { usePasskeyLogin } from '../hooks/usePasskeyLogin';

export const Route = createFileRoute('/login')({
  component: Login,
});

const passwordChecks = [
  {
    test: (value: string) => value.length >= 8,
    message: 'Be at least 8 characters long.',
  },
  {
    test: (value: string) => /[A-Z]/.test(value),
    message: 'Include at least one uppercase letter.',
  },
  {
    test: (value: string) => /[a-z]/.test(value),
    message: 'Include at least one lowercase letter.',
  },
  {
    test: (value: string) => /\d/.test(value),
    message: 'Include at least one number.',
  },
  {
    test: (value: string) => /[^A-Za-z0-9]/.test(value),
    message: 'Include at least one special character.',
  },
];

const getPasswordErrors = (value: string) =>
  passwordChecks.filter(({ test }) => !test(value)).map(({ message }) => message);

/**
 * CU-17tm466rmm6 / CU-17tm466rgr8 — is the patient doc REALLY absent?
 *
 * The listener's `hasResolved` flips on ANY first snapshot, a cached one
 * included: ✅ measured on firebase 11.10.0, a blocked Listen channel yields
 * `{ exists: false, fromCache: true }` after 40 ms (closed port) to 10 s
 * (silent server). Minting on that resets an established patient's account
 * state (until functions PR #399 it reset their whole chart; it still re-runs
 * the repair and re-sends a verification email to an unverified patient).
 *
 * So before minting, ask the SERVER once. `getDocFromServer` never answers
 * from cache: it resolves only with a server-sourced snapshot and rejects when
 * the server cannot be reached. Three outcomes, and only one mints:
 *   - 'absent'  → a genuine orphan (auth user, no doc): mint, as before;
 *   - 'exists'  → the cache lied; the listener delivers the doc and routes;
 *   - 'unknown' → offline or blocked: DO NOT mint. A mint cannot help a patient
 *                 whose Firestore channel is down, and that channel state is
 *                 exactly what produced the false trigger. The stuck recovery
 *                 (12 s) remains the exit. Reported, tagged, uid only.
 */
async function confirmAbsentOnServer(uid: string): Promise<'absent' | 'exists' | 'unknown'> {
  try {
    const snap = await getDocFromServer(doc(db, 'users', uid));
    return snap.exists() ? 'exists' : 'absent';
  } catch (error) {
    const code = (error as { code?: string })?.code ?? 'unknown';
    Sentry.addBreadcrumb({
      category: 'patient-creation',
      level: 'warning',
      message: 'login self-heal skipped: server read failed',
      data: { code },
    });
    // A breadcrumb alone never sends; this is the counter for how often the
    // self-heal would have fired on a bad connection. Tagged (Sentry
    // aggregates on tags); never the email. ⚠️ Anonymous at /login: the user
    // scope is set in _authenticated.tsx, below this route (review F19). A
    // counter is what this is for; set the user here first if attribution is
    // ever wanted.
    Sentry.captureMessage('login self-heal skipped: server read failed', {
      level: 'warning',
      tags: { selfHeal: 'server-read-failed' },
      extra: { code },
    });
    return 'unknown';
  }
}

function Login() {
  // LOCAL STATE FOR USERNAME AND PASSWORD
  const [email, setEmail] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [createEmailPassword, setCreateEmailPassword] = React.useState(false);
  const [useEmailPassword, setUseEmailPassword] = React.useState(false);
  const [password, setPassword] = React.useState('');
  const [working, setWorking] = React.useState(false);
  // ECN Subtask 8 (CU-86ahjxqvp): escape hatch for the loading/redirect screen.
  // The "Finishing Account Setup" state (agent-doc propagation race) and other
  // loading messages assume a transient wait — but if propagation stalls, a user
  // with valid auth cookies can get stuck indefinitely with no way out. After a
  // timeout we surface a prominent recovery; a low-key sign-out is always offered.
  const [showStuckRecovery, setShowStuckRecovery] = React.useState(false);
  /**
   * The pending MFA challenge, when sign-in returned one (CU-86ajwpnek §7).
   *
   * ⚠️ LOCAL STATE ONLY, NEVER ROUTED TO. The resolver is a live object holding
   * the in-flight sign-in; it does not survive serialization through router
   * params or storage. A reload loses it and returns the patient to a clean
   * sign-in form, where their password still works — they are never trapped.
   *
   * ⚠️ While this is set, `currentUser` is still NULL: the MFA error means
   * sign-in did NOT complete, so `onAuthStateChanged` has not fired. Every
   * effect below is already gated on `currentUser`, so the challenge is
   * invisible to the requestedURL / pendingSignOut / uidMismatch machinery —
   * and `requestedURL` survives untouched to be consumed after the challenge
   * completes, exactly as it is on an ordinary login.
   */
  const [mfaResolver, setMfaResolver] = React.useState<Resolver | null>(null);
  // Latched once the mint is issued. Since CU-17tm466rmm6 the re-entry guard
  // on this mount is `serverAbsenceCheck` below (set before the server read);
  // this ref is now belt-and-braces and documents the mint itself.
  const createPatientCalled = React.useRef(false);
  // ECN Subtask 8 (CU-86ahjxqvp): latched when the createPatient CF reports an
  // agent-account is pending (failed-precondition). Stops us re-calling the mint
  // on subsequent listener snapshots while we wait for the agent doc to propagate.
  const agentAccountPending = React.useRef(false);
  // CU-17tm466rmm6: ONE server-absence check per mount. Never cleared: a
  // re-run of the effect with the same "no doc" state must not re-check, or a
  // failing server read would loop (and report) on every render. 'exists'
  // resolves itself when the listener delivers the doc; 'unknown' ends in the
  // stuck recovery, whose exit is a reload.
  const serverAbsenceCheck = React.useRef(false);

  // AUTH HANDLING
  const {
    currentUser,
    firebaseLogin,
    // firebaseSignInWithRedirect,
    firebaseSignUp,
    firebaseSignOut,
  } = useAuth();
  /**
   * Passkey login (CU-86ajzr94n) — a PARALLEL ENTRY POINT, not a stage in the
   * password → MFA chain above.
   *
   * ⚠️ THIS MUST NEVER GATE THE PASSWORD PATH. `supported` only decides whether
   * the passkey BUTTON renders; every password and SMS control below renders
   * regardless of what this hook returns, including when it errors. That is the
   * standing non-negotiable for this workstream, and it is why the hook is
   * allowed to fail in any way at all without stranding a patient.
   *
   * ⚠️ NO NAVIGATION HERE. `onSuccess` deliberately does nothing but clear the
   * form state: the token exchange fires `onAuthStateChanged`, which sets
   * `currentUser`, and the EXISTING nav effect below consumes `requestedURL`
   * exactly as it does after a password sign-in. Navigating here as well would
   * be the second `navigate()` the console measured.
   */
  const {
    supported: passkeySupported,
    busy: passkeyBusy,
    error: passkeyError,
    signInWithPasskey,
    clearError: clearPasskeyError,
    armConditional,
  } = usePasskeyLogin(() => {
    setMessage('Signing You In');
  });

  /**
   * Arm conditional mediation once the email field is actually in the DOM —
   * adversarial round 2, F1.
   *
   * ⚠️ THE FIELD IS BEHIND THE `useEmailPassword` TOGGLE, so it does not exist
   * when this component mounts. `startAuthentication({ useBrowserAutofill })`
   * checks for `input[autocomplete$='webauthn']` FIRST and throws when it finds
   * none, and the conditional path swallows that silently — so arming at hook
   * mount meant the patient got no autofill at all until a re-arm tick up to
   * 50s later, exactly while they were typing their password.
   *
   * Runs on every render of the open panel; `armConditional` is idempotent.
   */
  React.useEffect(() => {
    if (useEmailPassword) armConditional();
  }, [useEmailPassword, armConditional]);

  const patientDocQuery = usePatientDoc(currentUser?.uid ?? '', { enabled: !!currentUser });
  const patientDocData = patientDocQuery.data as PatientDoc;
  const { hasResolved: patientDocResolved } = patientDocQuery;
  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await firebaseLogin(email, password);
    } catch (error) {
      // ⚠️ THIS BRANCH MUST COME FIRST, AND THE PASSWORD WAS CORRECT.
      //
      // Identity Platform throws `auth/multi-factor-auth-required` when the
      // credentials are VALID but the account holds a second factor. It is
      // control flow, not a failure. The blanket catch that used to be here
      // rendered "We couldn't log you in" over a perfectly good password — the
      // identical bug locked an enrolled admin out of the console on staging
      // (2026-07-30) and locked out mobile's test account before that.
      //
      // Once patients are enrolled this is the ROUTINE path for every one of
      // them, not an edge case (CU-86ajwpnek §7).
      const code = (error as { code?: string })?.code ?? '';
      if (code === 'auth/multi-factor-auth-required') {
        try {
          // ⚠️ The resolver is a LIVE object carrying the pending sign-in. It
          // cannot be serialized — never put it in router state, sessionStorage
          // or a URL. Local state only.
          setMfaResolver(getMultiFactorResolver(auth, error as MultiFactorError) as unknown as Resolver);
          // Deliberately NOT captured to Sentry: this is expected control flow
          // and would otherwise fire on every enrolled patient's every login.
          Sentry.addBreadcrumb({
            category: 'mfa-sms',
            level: 'info',
            message: 'login: second factor required',
          });
        } catch (resolverError) {
          // The resolver could not be built from a genuine MFA challenge — the
          // patient cannot proceed and cannot self-diagnose, so this must be
          // visible.
          Sentry.captureException(resolverError, {
            tags: { feature: 'mfa-sms', step: 'login-resolver' },
          });
          setMessage(
            "This account needs an extra verification step, but we couldn't start it. Please try again, or contact info@mideohealth.com.",
          );
          setWorking(false);
        }
        return;
      }

      alert(
        "We couldn't log you in. Please try again. Or if you're having trouble, please reach out to info@mideohealth.com.",
      );
      setWorking(false);
    }
  };

  const handleCreateAccount = async (e: FormEvent) => {
    e.preventDefault();
    const checkResults = passwordChecks.map(({ test, message }) => {
      const passed = test(password);
      return `${passed ? '✓' : '✗'} ${message}`;
    });
    const errors = getPasswordErrors(password);
    if (errors.length) {
      alert(`Invalid Password\n\nPassword requirements:\n${checkResults.join('\n')}`);
      setPassword('');
      return;
    }
    try {
      await firebaseSignUp(email, password);
    } catch (error) {
      alert(
        "We couldn't create your account. Please try again. Or if you're having trouble, please reach out to info@mideohealth.com.",
      );
      setWorking(false);
    }
  };

  // NAVIGATION FROM TANSTACK ROUTER
  const navigate = useNavigate();

  // Show toast on mount if redirected here from a UID mismatch.
  React.useEffect(() => {
    if (sessionStorage.getItem('uidMismatch')) {
      toast.info('You were signed in as a different account. Please sign in to continue.');
    }
  }, []);

  // Flag cleanup: clear session flags once currentUser becomes null (sign-out
  // fully propagated via onAuthStateChanged). At this point the stale-render
  // danger is over — the nav effect will see !currentUser and won't navigate.
  // Clearing here ensures flags don't persist into the next login attempt.
  React.useEffect(() => {
    if (!currentUser) {
      if (sessionStorage.getItem('uidMismatch')) {
        sessionStorage.removeItem('uidMismatch');
      }
      if (sessionStorage.getItem('pendingSignOut')) {
        sessionStorage.removeItem('pendingSignOut');
      }
    }
  }, [currentUser]);

  // LISTEN FOR COMPONENT UPDATE
  React.useEffect(() => {
    // Skip while the stale user from a sign-out is still clearing.
    if (sessionStorage.getItem('uidMismatch') || sessionStorage.getItem('pendingSignOut')) {
      return;
    }

    // HANDLE REDIRECT TO REQUESTED URL
    // sessionStorage: set by $patientId UID mismatch guard (tab-scoped, auto-expires)
    // localStorage: set by _authenticated guard (persists across tabs/reloads)
    // ECN Subtask 8 (CU-86ahjxqvp): an agent-only user (isMIDEOFamilyMember &&
    // !isPatient) has no chart — route them straight to /sessions (their whole
    // shell) instead of the patient chart root. Without this they'd be sent to
    // /${uid} and the $patientId route guard would bounce them to /sessions; this
    // skips that round-trip and the brief patient-route mount.
    const isAgentOnly =
      !!patientDocData && patientDocData.isMIDEOFamilyMember === true && !patientDocData.isPatient;
    if (currentUser && isAgentOnly) {
      setMessage('Loading Your Sessions');
      navigate({ to: '/sessions' });
      return;
    }

    const requestedURL = sessionStorage.getItem('requestedURL') || window.localStorage.getItem('requestedURL');
    if (currentUser && patientDocData && requestedURL) {
      // ACCOUNT FOR DIFFERENCE BETWEEN PROD AND STAGING URLS
      const path = requestedURL.includes('com') ? requestedURL.split('com')[1] : requestedURL.split('app')[1];
      sessionStorage.removeItem('requestedURL');
      window.localStorage.removeItem('requestedURL');

      // Safety check: if the requestedURL contains a patientId that doesn't match
      // the logged-in user, don't redirect there (it would trigger the $patientId
      // UID mismatch guard and bounce them back to /login in a loop).
      // URL pattern: /<patientId>/... — extract the first path segment after root.
      const pathSegments = path?.split('/').filter(Boolean);
      const urlPatientId = pathSegments?.[0];
      if (urlPatientId && urlPatientId !== currentUser.uid) {
        setMessage('Your Home Page is Loading');
        navigate({
          to: `/${currentUser.uid}` as FileRouteTypes['to'],
        });
      } else {
        setMessage('Navigating to Requested Page');
        navigate({
          to: path as FileRouteTypes['to'],
        });
      }
    } else if (currentUser && patientDocData && !requestedURL) {
      setMessage('Your Home Page is Loading');
      navigate({
        to: `/${currentUser.uid}` as FileRouteTypes['to'],
      });
    }

    // Wait for the Firestore listener to resolve before deciding to create — without
    // this gate, the effect fires before the listener returns its first snapshot and
    // misinterprets "still loading" as "doc doesn't exist" (CU-86afvk179).
    //
    // CU-17tm466rmm6: a resolved listener with no data is NOT proof of absence
    // (a cached "doesn't exist" counts), so the server is asked once before
    // the mint. See confirmAbsentOnServer.
    if (
      currentUser &&
      patientDocResolved &&
      !patientDocData &&
      !createPatientCalled.current &&
      !agentAccountPending.current &&
      !serverAbsenceCheck.current
    ) {
      serverAbsenceCheck.current = true;
      setMessage('Checking Your Account');
      const uid = currentUser.uid;
      const email = currentUser.email ? currentUser.email : '';
      void confirmAbsentOnServer(uid).then((answer) => {
        if (answer === 'exists') {
          // The cache lied. The listener delivers the real doc and the
          // navigation branch above routes; nothing to mint.
          return;
        }
        if (answer === 'unknown') {
          // Offline or blocked: never mint on a guess. The stuck recovery
          // takes over after 12 s.
          setMessage('Connecting To Your Account');
          return;
        }
        createPatientCalled.current = true;
        setMessage('Setting Up Your Account');
        //CREATE PATIENT FIRESTORE DOCUMENT
        createPatient({ uid, email }).catch((error: Error & { code?: string }) => {
        // ECN Subtask 8 (CU-86ahjxqvp): the createPatient CF refuses to mint a
        // patient doc when a pending/accepted AgentRequest exists for this user —
        // this is an agent whose S4-provisioned doc hasn't yet reached our
        // listener (a write-propagation race), NOT a real failure. Do NOT alert.
        // Latch `agentAccountPending` so we stop re-calling the mint on later
        // listener snapshots; usePatientDoc is a live listener, so once the agent
        // doc propagates `patientDocData` populates and the navigation effect
        // routes them (the persona/route guard sends agents to /sessions).
          if (error.code === 'functions/failed-precondition') {
            agentAccountPending.current = true;
            setMessage('Finishing Account Setup');
            return;
          }
          Sentry.captureException(error);
          console.error('Error creating patient doc', error);
          setMessage('');
          alert('Error creating patient document. Please contact support.');
        });
      });
    }
  }, [currentUser, patientDocData, patientDocResolved, navigate]);

  // ECN Subtask 8 (CU-86ahjxqvp): bound the loading wait. If a loading message is
  // showing for more than 12s (the account-setup / redirect screen never resolved),
  // reveal the prominent stuck-recovery so the user is never trapped. Resets when
  // the message clears (normal flow) or changes to a fresh stage.
  React.useEffect(() => {
    if (!message) {
      setShowStuckRecovery(false);
      return;
    }
    const t = setTimeout(() => setShowStuckRecovery(true), 12000);
    return () => clearTimeout(t);
  }, [message]);

  // Escape hatch: clear Firebase Auth + the stale-session flags and return to a
  // clean login. Mirrors SignOutButton's canonical teardown.
  const handleEscapeHatch = async () => {
    sessionStorage.setItem('pendingSignOut', 'true');
    sessionStorage.removeItem('requestedURL');
    window.localStorage.removeItem('requestedURL');
    window.localStorage.removeItem('emailForSignIn');
    try {
      await firebaseSignOut();
    } catch (error) {
      Sentry.captureException(error);
    }
    // Hard reload to /login to drop any in-memory listener/ref state cleanly.
    window.location.assign('/login');
  };

  // ⚠️ THE CHALLENGE REPLACES the sign-in form rather than stacking over it, and
  // is checked BEFORE the `message` branch — otherwise a stale loading message
  // would hide a live challenge and strand the patient mid-sign-in.
  //
  // `currentUser` is still null here (the MFA error means sign-in did not
  // complete), so none of the redirect effects below have run.
  if (mfaResolver) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-6 px-4">
        <img
          src="https://storage.googleapis.com/mideoid-console-public/logos/MIDEOhealth.png"
          alt="mideo_logo"
          className="h-24"
        />
        <MfaChallengeForm
          resolver={mfaResolver}
          // CU-86akbcqv8 — the address typed at the password step, so a
          // challenge-leg failure can be tied to an attempt. ⚠️ A CLAIM, not a
          // verified identity: the second factor is unproven at this point.
          claimedEmail={email}
          onComplete={() => {
            // Clear the challenge and the password. `requestedURL` is left
            // ALONE — onAuthStateChanged now fires, and the existing nav effect
            // consumes it exactly as it does on an ordinary sign-in.
            setMfaResolver(null);
            setPassword('');
            setMessage('Signing You In');
          }}
          onCancel={() => {
            // The patient is never trapped: this returns to the sign-in form
            // where their password still works as the durable root.
            setMfaResolver(null);
            setPassword('');
            setMessage('');
            setWorking(false);
          }}
        />
      </div>
    );
  }

  return (
    <>
      {message.length > 0 ? (
        <div className="flex h-screen flex-col items-center justify-center">
          <img
            src="https://storage.googleapis.com/mideoid-console-public/logos/MIDEOhealth.png"
            alt="mideo_logo"
            className="h-36"
          />
          <p>{message}</p>

          {/* ECN Subtask 8 (CU-86ahjxqvp): prominent stuck-state recovery, revealed
              after a 12s timeout so a user whose account setup / redirect never
              resolves is never trapped with valid auth cookies. */}
          {showStuckRecovery && (
            <div className="mt-6 flex max-w-sm flex-col items-center gap-2 text-center">
              <p className="text-sm text-gray-600">
                This is taking longer than expected. You can sign out and try again.
              </p>
              <button
                type="button"
                onClick={handleEscapeHatch}
                className="rounded-md bg-mideo-darkred px-4 py-2 text-white hover:opacity-90"
              >
                Sign out and try again
              </button>
            </div>
          )}

          {/* Always-available low-key escape, even before the timeout. */}
          {!showStuckRecovery && (
            <button
              type="button"
              onClick={handleEscapeHatch}
              className="mt-4 text-sm text-gray-500 underline hover:text-gray-700"
            >
              Having trouble? Sign out
            </button>
          )}
        </div>
      ) : (
        <div className="mt-36 flex h-screen flex-col items-center">
          <img
            src="https://storage.googleapis.com/mideoid-console-public/logos/MIDEOhealth.png"
            alt="mideo_logo"
            className="h-36"
          />
          {/* <div className="mx-6 my-4 flex flex-col items-center">
            <ButtonGreen
              id="sign-in"
              type="button"
              onClick={async () => await firebaseSignInWithRedirect()}
              textContent="MIDEO Patients Login/Create Account"
              classNameOverride="my-4 rounded-md bg-sky-700 px-4 py-2 text-3xl text-white hover:bg-sky-800 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:shadow-none sm:px-8 sm:py-3"
            />
            <p className="text-center text-3xl italic">
              New and returning patients should click the above button and use their personal email to sign-in or create
              an account after reading the instructions{' '}
              <a className="font-bold text-blue-600 underline" href="/passwordless">
                here
              </a>
              .
            </p>
          </div> */}
          <div className="mx-6 my-4 flex flex-col items-center">
            <Button
              id="enroll"
              type="button"
              onClick={async () =>
                navigate({
                  to: '/enroll',
                  from: '/login',
                })
              }
              textContent="Partner Login"
            />
            <p className="text-center text-xl italic">
              Patients redeeming a benefit from their workplace or organization should click the above button and their
              company email and provided password to login.
            </p>
          </div>
          <div className="flex flex-row">
            <div className="mx-6 my-4 flex flex-col items-center">
              <ButtonGreen
                id="use-email-password"
                type="button"
                onClick={() => setUseEmailPassword(!useEmailPassword)}
                textContent="Login"
              />
              {useEmailPassword && (
                <div className="flex h-screen flex-col items-center">
                  <form>
                    <FormInput
                      id="username"
                      handleBlur={() => {}}
                      label="Email"
                      name="username"
                      value={email}
                      handleChange={(e) => {
                        setEmail(e.target.value);
                        // Editing the email is the patient moving on from a
                        // failed passkey attempt; the stale error must go.
                        clearPasskeyError();
                      }}
                      placeholder="Email"
                      /*
                       * ⚠️ `webauthn` MUST BE THE LAST TOKEN. The library selects
                       * `input[autocomplete$='webauthn']` — a SUFFIX match,
                       * confirmed in the installed bundle — and throws
                       * "Browser does not support WebAuthn autofill" if it finds
                       * no match. So `"username"` silently disables conditional
                       * mediation, and `"webauthn username"` ALSO disables it
                       * while still containing the word. A `toContain('webauthn')`
                       * assertion would pass both; the test asserts the last
                       * token for exactly that reason.
                       */
                      autoComplete="username webauthn"
                    />
                    <PasswordInput
                      id="password"
                      title="Password"
                      password={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <div className="flex flex-col">
                      <Button
                        id="submit"
                        type="submit"
                        onClick={(e) => handleLogin(e)}
                        textContent="Login"
                        disabled={working}
                        working={working}
                        workingText="Logging in..."
                      />
                      <RequestPasswordReset email={email} />
                      <RequestSetPassword email={email} />
                    </div>
                  </form>
                </div>
              )}
            </div>
            <div className="mx-6 my-4 flex flex-col items-center">
              <ButtonGreen
                id="create-email-password"
                type="button"
                onClick={() => setCreateEmailPassword(!createEmailPassword)}
                textContent="New Patient"
              />
              {createEmailPassword && (
                <div className="flex h-screen flex-col items-center">
                  <form>
                    <FormInput
                      id="username"
                      handleBlur={() => {}}
                      label="Email"
                      name="username"
                      value={email}
                      handleChange={(e) => setEmail(e.target.value)}
                      placeholder="Email"
                    />

                    <PasswordInput
                      id="password"
                      title="Password"
                      password={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <ul className="my-2 text-sm text-gray-600">
                      <li className="font-semibold">Password must:</li>
                      {passwordChecks.map(({ message }) => (
                        <li key={message}>• {message}</li>
                      ))}
                    </ul>
                    <div className="flex flex-col">
                      <Button
                        id="submit"
                        type="submit"
                        onClick={(e) => handleCreateAccount(e)}
                        textContent="Create Account"
                        disabled={working}
                        working={working}
                        workingText="Creating Account..."
                      />
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>

          {/*
            ⚠️ TOP LEVEL, AND OUTSIDE `useEmailPassword` — adversarial round 2,
            F1. This block used to be nested inside the password panel, so a
            patient who wanted to sign in with a passkey had to click "Login"
            and open the password form FIRST. A passkey is a way to AVOID the
            password form, not a feature inside it.

            🔴 `passkeySupported` GATES ONLY THIS BLOCK, AND THAT PROPERTY
            SURVIVED THE MOVE. Every password control — the Login and New
            Patient toggles above, the email and password inputs, the submit
            button, the reset links — renders outside it and is unconditional.
            Pinned by "🔴 the password path is never gated on passkeys" and by
            the mutant that gates the password form on passkey support.
          */}
          {passkeySupported && (
            <div className="mx-6 mb-8 flex flex-col items-center gap-2">
              <ButtonGreen
                id="passkey-login"
                type="button"
                onClick={() => void signInWithPasskey()}
                textContent={passkeyBusy ? 'Waiting for your passkey...' : 'Sign in with a passkey'}
                disabled={passkeyBusy}
              />
              {passkeyError && (
                <p role="alert" className="max-w-xs text-center text-sm text-mideo-darkred">
                  {/*
                    ⚠️ THE COPY MUST NOT ASSERT A REASON on `failed`. The server
                    returns ONE uniform error for every failure so a logged-out
                    caller cannot probe what an account holds — a revoked
                    credential, an expired challenge and a wrong signature are
                    indistinguishable here.
                  */}
                  {passkeyError.kind === 'no-credentials'
                    ? 'This device has no passkey saved for MIDEO. Sign in with your password, or use the device where you set one up.'
                    : passkeyError.kind === 'network'
                      ? 'We could not reach MIDEO. Check your connection and try again.'
                      : passkeyError.kind === 'unsupported'
                        ? 'This browser cannot use passkeys. Please sign in with your password.'
                        : 'That passkey did not work. Please try again, or sign in with your password.'}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
